import { Dispatch } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import i18n from 'i18next';
import {
  setEmsMemberAccountDetail,
  setEmsMemberAccountList,
  setEmsMemberAccountListNotForLogin
} from '../eaiSlice';
import {
  createGetHeaderData,
  createHeaderData,
  createPutHeaderData,
  createQueryParams,
  handlePreconditionFailed
} from 'utils/restUtils';
import { apiClient } from 'services/restService';
import { openSnackbar } from 'features/app/appSlice';
import { HttpHeaders } from 'models';
import { AppThunk } from 'models/store';
import {
  CreateEmsMemberAccountRequestBody,
  unassignMemberAccountRequestBody,
  assignMemberAccountRequestBody
} from '../eaiTypes';
import { EaiEtagEntityList } from 'models/etag';
import { logError } from 'services/Logging';

// ==============================|| EMS MEMBER ACCOUNT THUNKS ||============================== //

export const fetchAndSetEmsMemberAccountList =
  (
    isLogin = false,
    userId: string | undefined = undefined,
    includeHistory = false,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<
        | typeof setEmsMemberAccountList
        | typeof setEmsMemberAccountListNotForLogin
      >
    >
  ) => {
    const headers: HttpHeaders = createGetHeaderData(
      !isLogin,
      'application/hal+json'
    );
    const queryParams = createQueryParams({
      isLogin: isLogin,
      userId: userId,
      includeHistory: includeHistory
    });

    apiClient
      .get(`/eai/ems-member-account/member-accounts${queryParams}`, {
        headers
        // data: { showAjaxLoader: false }
      })
      .then((response: AxiosResponse) => {
        if (isLogin) {
          dispatch(setEmsMemberAccountList(response.data));
        } else {
          dispatch(setEmsMemberAccountListNotForLogin(response.data));
        }

        callback && callback();
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getEmsMemberAccountDetail =
  (EmsMemberAccountID: string | bigint | undefined): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setEmsMemberAccountDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`/eai/ems-member-account/detail/${EmsMemberAccountID}`, {
        headers,
        data: { etagEntity: EaiEtagEntityList.EMS_MEMBER_ACCOUNT }
      })
      .then((response: AxiosResponse) => {
        dispatch(setEmsMemberAccountDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const createEmsMemberAccount =
  (
    requestBody: CreateEmsMemberAccountRequestBody,
    callback?: (id: string | bigint) => void
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');

    apiClient
      .post('/eai/ems-member-account', { ...requestBody }, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.createEmsMemberAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data.emsPartyId);
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateEmsMemberAccount =
  (
    name: string,
    EmsMemberAccountID?: string | bigint,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof getEmsMemberAccountDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `/eai/ems-member-account/detail/${EmsMemberAccountID || ''}`,
        { name: name },
        { headers }
      )
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateEmsMemberAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback
          ? callback()
          : dispatch(getEmsMemberAccountDetail(response.data.id));
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(getEmsMemberAccountDetail(EmsMemberAccountID)),
          () =>
            dispatch(
              updateEmsMemberAccount(
                name,
                EmsMemberAccountID,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const activateEmsMemberAccount =
  (EmsMemberAccountID: string | bigint, etag?: string, callback?: () => void) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof getEmsMemberAccountDetail | typeof openSnackbar>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .patch(
        `/eai/ems-member-account/${EmsMemberAccountID}/activate`,
        {},
        {
          headers
        }
      )
      .then((response: AxiosResponse) => {
        dispatch(getEmsMemberAccountDetail(response.data.id));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.activateEmsMemberAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(getEmsMemberAccountDetail(EmsMemberAccountID));

            callback && callback();
          },
          () =>
            dispatch(
              activateEmsMemberAccount(EmsMemberAccountID, undefined, callback)
            )
        );
        logError(error);
      });
  };

export const deactivateEmsMemberAccount =
  (EmsMemberAccountID: string | bigint, etag?: string, callback?: () => void) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof getEmsMemberAccountDetail | typeof openSnackbar>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .patch(
        `/eai/ems-member-account/${EmsMemberAccountID}/deactivate`,
        {},
        {
          headers
        }
      )
      .then((response: AxiosResponse) => {
        dispatch(getEmsMemberAccountDetail(response.data.id));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.deactivateEmsMemberAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(getEmsMemberAccountDetail(EmsMemberAccountID));

            callback && callback();
          },
          () =>
            dispatch(
              deactivateEmsMemberAccount(
                EmsMemberAccountID,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const changeEmsMemberAccountSubscription =
  (
    EmsMemberAccountID: string | bigint | undefined,
    serviceLevelSpecificationId: string,
    etag?: string,
    callback?: () => any
  ) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof getEmsMemberAccountDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );
    apiClient
      .patch(
        `/eai/ems-member-account/${EmsMemberAccountID}/change-subscription`,
        { serviceLevelSpecificationId: serviceLevelSpecificationId },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(getEmsMemberAccountDetail(EmsMemberAccountID));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.eai.messages.changeEmsMemberAccountSubscription'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(getEmsMemberAccountDetail(EmsMemberAccountID)),
          () =>
            dispatch(
              changeEmsMemberAccountSubscription(
                EmsMemberAccountID,
                serviceLevelSpecificationId,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const assignEmsMemberAccountUser =
  (
    requestBody: assignMemberAccountRequestBody,
    EmsMemberAccountID: string | bigint | undefined,
    callback?: () => any
  ) =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createPutHeaderData(
      true,
      'application/hal+json'
    );
    apiClient
      .put(
        `/eai/ems-member-account/${EmsMemberAccountID}/assign-user`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.assignEmsMemberAccountUser'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => logError(error));
  };

export const unassignEmsMemberAccountUser =
  (
    requestBody: unassignMemberAccountRequestBody,
    EmsMemberAccountID: string | bigint | undefined,
    callback?: () => any
  ) =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createPutHeaderData(
      true,
      'application/hal+json'
    );
    apiClient
      .patch(
        `/eai/ems-member-account/${EmsMemberAccountID}/unassign-user`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.unassignEmsMemberAccountUser'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => logError(error));
  };
